import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Image from '../../Image/Image';

const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVector.svg';

const SeoDetailDirtyLoadModuleComponent = ({ data }) => {
  const { title, description, footer, cards = [], faq = [] } = data || {};
  const hasFAQ = Array.isArray(faq) && faq.length > 0;

  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '802px',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          p: '32px',
          gap: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '3rem',
            lineHeight: '3.25rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            textAlign: 'center',
            whiteSpace: 'pre-line',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: title || '' }}
        />

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.5rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            textAlign: 'center',
            maxWidth: '738px',
            whiteSpace: 'pre-line',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: description || '' }}
        />

        {/* Cards */}
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              sx={{
                width: '1010px',
                backgroundColor: '#FFFFFF',
                borderRadius: '48px',
                boxShadow:
                  '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                boxSizing: 'border-box',
                p: '40px',
                display: 'flex',
                gap: '40px',
              }}
            >
              {/* Number Icon */}
              <Box
                sx={{
                  minWidth: '100px',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{ position: 'relative', width: '96px', height: '96px' }}
                >
                  <Image
                    width={96}
                    height={96}
                    src={KEYPOINT_VECTOR_URL}
                    alt="Key point illustration"
                    sx={{ width: '100%', height: '100%' }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontFamily: 'Pally, Inter',
                      letterSpacing: '-0.03em',
                      fontWeight: 700,
                      fontSize: '4.5rem',
                      lineHeight: '4.25rem',
                      color: '#162F3B',
                      pointerEvents: 'none',
                    }}
                  >
                    {card.number}
                  </Typography>
                </Box>
              </Box>

              {/* Card Content */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '730px',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    lineHeight: '1.75rem',
                    color: '#162F3B',
                    '& p': { margin: 0 },
                  }}
                  dangerouslySetInnerHTML={{ __html: card.title || '' }}
                />

                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '1.5rem',
                    color: '#162F3B',

                    '& p': { m: 0, mb: '1em', mt: '1em' },
                    '& p:last-of-type': { mb: 0 },
                    '& strong': { fontWeight: 700 },
                    '& ul': {
                      m: 0,
                      mt: '1em',
                      pl: 0,
                      listStylePosition: 'inside',
                    },
                    /* ✅ hyperlink style */
                    '& a': {
                      color: '#C7CB00',
                      textDecoration: 'underline',
                      fontWeight: 700,
                    },
                    '& a:hover': {
                      opacity: 0.85,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: card.description || '' }}
                />
              </Box>
            </Box>
          </ScrollFadeWrapper>
        ))}

        {/* FAQ */}
        {hasFAQ && (
          <Box
            sx={{
              width: '1010px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {faq.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <Box
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(i);
                    }
                  }}
                  sx={{
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '24px',
                    boxShadow:
                      '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                    p: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <IconButton
                      disableRipple
                      tabIndex={-1}
                      sx={{
                        width: '32px',
                        height: '32px',
                        color: '#162F3B',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease',
                      }}
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      <ArrowDropDownIcon sx={{ fontSize: '32px' }} />
                    </IconButton>
                  </Box>

                  <Collapse in={isOpen} timeout={250} unmountOnExit>
                    <Typography
                      sx={{
                        mt: '16px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.02em',
                        color: '#162F3B',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Footer */}
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#162F3B',
            maxWidth: '473px',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: footer || '' }}
        />
      </Box>
    </Box>
  );
};

export default SeoDetailDirtyLoadModuleComponent;
